import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Zap, Save } from 'lucide-react';
import { useDeviceStore } from '../../stores/deviceStore';
import { useScriptStore } from '../../stores/scriptStore';
import { useUIStore } from '../../stores/uiStore';
import { ConditionType } from '../../api/types';

export function ScriptEditorScreen() {
  const navigate = useNavigate();
  const { devices } = useDeviceStore();
  const { addScript } = useScriptStore();
  const { addToast } = useUIStore();

  const [formData, setFormData] = useState({
    name: '',
    triggerDeviceId: '',
    triggerParameter: '',
    triggerCondition: ConditionType.AndCondition,
    triggerValue: '',
    targetDeviceId: '',
    targetParameter: '',
    targetValue: '',
    isActive: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    await addScript({
      name: formData.name,
      type: 'device_trigger' as any,
      isActive: formData.isActive,
      ifDevices: [{
        deviceId: formData.triggerDeviceId,
        params: {
          parameterName: formData.triggerParameter,
          stringValue: formData.triggerValue,
        }
      }] as any,
      thenDevices: [{
        deviceId: formData.targetDeviceId,
        params: {
          parameterName: formData.targetParameter,
          stringValue: formData.targetValue,
        }
      }] as any,
      actionId: Date.now().toString(),
      id: Date.now().toString(),
      flatId: 1,
      conditionType: formData.triggerCondition,
      groupDeviceId: '',
      groupName: '',
    });
    
    addToast('Сценарий создан', 'success');
    navigate('/scripts');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/scripts')}
              className="p-2 -ml-2 rounded-full hover:bg-gray-100"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </button>
            
            <h1 className="text-xl font-bold text-gray-900">Новый сценарий</h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Название сценария
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Например: Включить свет при движении"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            required
          />
        </div>

        {/* Trigger */}
        <div className="bg-blue-50 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-blue-600" />
            <h2 className="font-semibold text-blue-900">Когда (условие)</h2>
          </div>
          
          <select
            value={formData.triggerDeviceId}
            onChange={(e) => setFormData({ ...formData, triggerDeviceId: e.target.value, triggerParameter: '' })}
            className="w-full px-3 py-2 border border-blue-200 rounded-lg mb-3"
            required
          >
            <option value="">Выберите устройство...</option>
            {devices.map(device => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.customName || device.baseName} ({device.room})
              </option>
            ))}
          </select>

          {formData.triggerDeviceId && (
            <>
              <select
                value={formData.triggerParameter}
                onChange={(e) => setFormData({ ...formData, triggerParameter: e.target.value })}
                className="w-full px-3 py-2 border border-blue-200 rounded-lg mb-3"
                required
              >
                <option value="">Выберите параметр...</option>
                {devices.find(d => d.deviceId === formData.triggerDeviceId)?.parameters.map(param => (
                  <option key={param.name} value={param.name}>
                    {param.rusName || param.name}
                  </option>
                ))}
              </select>

              <input
                type="text"
                value={formData.triggerValue}
                onChange={(e) => setFormData({ ...formData, triggerValue: e.target.value })}
                placeholder="Значение"
                className="w-full px-3 py-2 border border-blue-200 rounded-lg"
                required
              />
            </>
          )}
        </div>

        {/* Action */}
        <div className="bg-green-50 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-5 h-5 text-green-600" />
            <h2 className="font-semibold text-green-900">Тогда (действие)</h2>
          </div>
          
          <select
            value={formData.targetDeviceId}
            onChange={(e) => setFormData({ ...formData, targetDeviceId: e.target.value, targetParameter: '' })}
            className="w-full px-3 py-2 border border-green-200 rounded-lg mb-3"
            required
          >
            <option value="">Выберите устройство...</option>
            {devices.map(device => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.customName || device.baseName} ({device.room})
              </option>
            ))}
          </select>

          {formData.targetDeviceId && (
            <>
              <select
                value={formData.targetParameter}
                onChange={(e) => setFormData({ ...formData, targetParameter: e.target.value })}
                className="w-full px-3 py-2 border border-green-200 rounded-lg mb-3"
                required
              >
                <option value="">Выберите параметр...</option>
                {devices.find(d => d.deviceId === formData.targetDeviceId)?.parameters.map(param => (
                  <option key={param.name} value={param.name}>
                    {param.rusName || param.name}
                  </option>
                ))}
              </select>

              <input
                type="text"
                value={formData.targetValue}
                onChange={(e) => setFormData({ ...formData, targetValue: e.target.value })}
                placeholder="Установить значение..."
                className="w-full px-3 py-2 border border-green-200 rounded-lg"
                required
              />
            </>
          )}
        </div>

        <button
          type="submit"
          className="w-full py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 flex items-center justify-center gap-2"
        >
          <Save className="w-5 h-5" />
          Создать сценарий
        </button>
      </form>
    </div>
  );
}
